/**
 * 游戏场景（核心）：关卡流 + 全部运行时机制接线。
 *
 * ============================ 关卡模块契约 ============================
 * 关卡模块路径：public/js/content/stages/stageN.js（N=1..6），默认导出：
 *   { id, name, bg, bgm, *script(api) }
 *   bg/bgm 为 manifest key；script 为 generator，按序 yield 指令对象：
 *     {wait: 秒}            等待
 *     {spawn: enemyDef}     生成一个小兵（计入 chapter 击破率分母）
 *     {wave: [enemyDef...]} 生成一波
 *     {clear: true}         等待非 boss 敌全灭
 *     {boss: bossDef}       生成 Boss 并等待击破（bossDef 见 game/enemies.js Boss）
 *     {say: '文本'}         底部台词框（2.5s，非阻塞）
 * 模块形状校验失败（缺 script）时兜底为内置测试关（3 波小怪 + 2 阶段 boss，见 content/stages/fallback.js）。
 *
 * enemyDef.script(e, dt, ctx) 与 boss phase.update(boss, dt, ctx) 收到的 ctx：
 *   { field, playerShots, enemies, items, effects, state, scoring, audio, loop,
 *     player, mods, timers, assets, t, playerX, playerY, stageIndex, rand,
 *     fireGate(e, interval) }
 * 敌机射击请用 ctx.fireGate(e, 间隔秒) 判定——它统一遵守角色技能效果：
 *   mods.sleepT>0（困困·梦境领域）不射弹；mods.confuseT>0（惑惑·混乱力场）射速减半。
 * mods 计时器字段（gameScene 每帧衰减，角色技能见 content/characters.js）：
 *   rageT / optionBoostT / confuseT / jitterT / sleepT / shieldT / dashT
 * ====================================================================
 */
import { VIEW_W, VIEW_H } from '../config.js';
import { Input } from '../engine/input.js';
import { TAU, randRange, rand, setRng, mulberry32 } from '../engine/math.js';
import { BulletField, PlayerShots } from '../game/bullets.js';
import { Enemies, Boss } from '../game/enemies.js';
import { readSpellHistory, recordSpellResult, spellKey, spellIndexOf } from '../game/spellHistory.js';
import { Items } from '../game/items.js';
import { Effects } from '../game/effects.js';
import { Player } from '../game/player.js';
import { drawHud } from '../game/hud.js';
import { DEFAULT_LIVES, DEFAULT_BOMBS, POWER_MAX, POWER_PER_ITEM } from '../game/state.js';
import * as patterns from '../game/patterns.js';
import { getDifficulty, difficultyIndex, EXTRA_DIFF } from '../content/difficulties.js';
import { CHARACTERS } from '../content/characters.js';
import * as BF from '../content/stages/bosses.js';
import { makeFallbackStage } from '../content/stages/fallback.js';
import { STAGE_MODULES, stageExtra } from '../content/stages/index.js';
import { modsGate, gatedInterval } from '../game/modsGate.js';
import { menuNav } from '../engine/menu.js';
import { wrapText } from '../engine/wrapText.js';
import { Assets } from '../engine/assets.js';
import { Recorder, Replayer } from '../replay.js';
import { t, tf, L, charName, diffName } from '../i18n.js';
import { STAGE_DIALOGUE, dialogueText } from '../content/dialogue.js';

/** 关卡数 */
const STAGE_COUNT = 6;
/** 背景滚动速度（px/s） */
const BG_SCROLL_SPEED = 40;
/** 关卡标题卡时长（秒） */
const TITLE_CARD_T = 3;
/** 结算卡时长（秒） */
const TALLY_T = 6;
/** 续关倒计时（秒） */
const CONTINUE_T = 10;
/** 最大续关次数 */
const MAX_CONTINUES = 3;
/** 暂停菜单项（文案 id） */
const PAUSE_ITEMS = ['pause.resume', 'pause.restart', 'pause.toTitle'];

export const GameScene = {
  mgr: null,

  // ---------------------------------------------------------------- 生命周期

  enter(params) {
    const shared = this.mgr.shared;
    this.audio = shared.audio;
    this.assets = shared.assets;
    this.state = shared.state;
    // 回放模式：角色/难度/模式取自录像元数据
    this.replay = params.replay || null;
    if (this.replay) {
      params = {
        character: CHARACTERS.find((c) => c.id === this.replay.character),
        difficulty: this.replay.difficulty,
        mode: this.replay.mode,
        practice: this.replay.practice,
        replay: this.replay,
      };
    }
    this.character = params.character;
    // 符卡练习：单符卡、满火力、1 命、无续关
    this.practice = params.practice || null;
    // 难度：params.difficulty 可为定义对象或 id 字符串，未知值回落 Normal
    const pd = params.difficulty;
    this.diff = getDifficulty(pd && (pd.id || pd));
    this.diffIdx = difficultyIndex(this.diff);
    // Extra 模式：单关长 boss 战，无续关，弹幕结构按 Hard 档分支
    this.extra = params.mode === 'extra';
    if (this.extra || (this.practice && this.practice.stageIndex === 6)) this.diffIdx = 2; // Extra(含练习) 弹幕结构按 Hard 档（参数已按 Hard 基准迁移；见 MR !41）
    // 确定性随机源：每局一个种子（回放用录像种子，保证逐帧一致）
    this.seed = this.replay ? this.replay.seed
      : params.seed !== undefined ? params.seed
        : ((Date.now() ^ Math.floor(Math.random() * 0x7fffffff)) >>> 0);
    setRng(mulberry32(this.seed));
    this.state.reset(this.character, this.diff);
    this.state.scoring.scoreMul = this.diff.scoreMul; // 难度分数系数
    if (this.practice) {
      this.state.power = POWER_MAX; // 练习模式默认满火力
      this.state.lives = 1; // 1 命
    }

    // 录像/回放驱动
    if (this.replay) {
      this.replayer = new Replayer(this.replay.frames);
      this.recorder = null;
    } else {
      this.replayer = null;
      this.recorder = new Recorder({
        seed: this.seed,
        character: this.character.id,
        difficulty: this.diff.id,
        mode: this.practice ? (this.practice.stageIndex === 6 ? 'extra' : 'practice') : this.extra ? 'extra' : 'normal', // 内容定版本档：Ex 练习按 extra
      });
    }

    this.field = new BulletField();
    this.field.speedMul = this.diff.speed;
    const exLike = this.extra || (this.practice && this.practice.stageIndex === 6);
    if (exLike) {
      // Extra 与 Ex 练习统一按 Lunatic 档：先归索引（auxMul 旋速/追踪、墙缺口也按 L），
      // 再覆盖弹量系数（EXTRA_DIFF.density=3.4）
      patterns.setPatternDifficulty(2);
      patterns.setPatternDensity(this.diff.density);
    } else {
      patterns.setPatternDifficulty(this.diffIdx); // 弹幕结构按难度分支（patterns.js）
    }
    this.playerShots = new PlayerShots();
    this.enemies = new Enemies();
    this.items = new Items();
    this.effects = new Effects();
    this.effects.renderer = shared.renderer;
    this.player = new Player();
    this.player.reset(VIEW_W / 2, VIEW_H - 80, this.character);
    this.items.onCollect = (it) => this._onCollect(it);

    // 角色技能计时器（字段约定见 characters.js 顶部）
    this.mods = {
      rageT: 0, optionBoostT: 0, confuseT: 0, jitterT: 0,
      sleepT: 0, shieldT: 0, dashT: 0,
    };
    this.player.mods = this.mods; // shot.spawn 经 player.mods 读狂暴/子机强化
    this.timers = [];
    this.boss = null;
    this._fieldProxy = null;

    this.stageIndex = 0;
    this.continues = 0;
    this.bombFragments = 0;
    this.maxPointV = this.state.scoring.pointV;
    this.t = 0;
    this.bgScroll = 0;
    this.mode = 'play'; // 'play' | 'tally' | 'continue'
    this.paused = false;
    this.pauseIdx = 0;
    this.message = null;
    this.titleCardT = 0;
    this.bossBannerT = 0;
    this.phaseBanner = null; // 阶段宣言横幅（onPhaseChange 设置）
    this.loading = true;

    this._grazeSeen = 0;
    this._shotsSeen = 0;
    this._jitterMark = 0; // 思维混乱已处理到的敌弹 id 水位（id 单调递增，天然去重）

    const scene = this;
    this.ctx = {
      Input,
      state: this.state,
      scoring: this.state.scoring,
      field: this.field,
      playerShots: this.playerShots,
      enemies: this.enemies,
      items: this.items,
      effects: this.effects,
      audio: this.audio,
      loop: shared.loop,
      player: this.player,
      mods: this.mods,
      timers: this.timers,
      assets: this.assets,
      rand, // 走可注入随机源（录像/回放确定性）
      VIEW_W,
      VIEW_H,
      t: 0,
      playerX: this.player.x,
      playerY: this.player.y,
      stageIndex: 0,
      diffIdx: this.diffIdx, // 关卡/ boss 脚本的难度分支开关
      fireGate: (e, interval) => scene._fireGate(e, interval),
    };

    modsGate.mods = this.mods; // 敌机射击全局门控挂接（fireTimer 全调用点生效）
    this._startStage(this.practice ? this.practice.stageIndex : 0);
  },

  exit() {
    const shared = this.mgr.shared;
    if (shared.loop) shared.loop.timeScale = 1; // 子弹时间兜底恢复
    patterns.setPatternDifficulty(1); // 弹幕难度分支兜底归 Normal
    modsGate.mods = null; // 摘除射击门控（避免泄漏到其他场景/校验进程）
    setRng(null); // 随机源恢复默认
    // 回放结束/中断后清空输入：Replayer 注入的按键状态不能带进下一场景
    if (this.replayer) Input._reset();
    this.audio.stopBgm();
  },

  // ---------------------------------------------------------------- 关卡装载

  _loadStageModule(idx) {
    // 静态注册表（content/stages/index.js）：同步取值，无动态 import
    const mod = (this.extra || (this.practice && idx === 6)) ? stageExtra : STAGE_MODULES[idx];
    if (mod && typeof mod.script === 'function') return mod;
    return makeFallbackStage(idx);
  },

  _startStage(idx) {
    this.stageIndex = idx;
    this.state.stageIndex = idx;
    // 清上一关的横幅残留：符卡宣言/boss 横幅/台词框在续关/重开后会叠在新标题卡上（历史 bug）
    this.phaseBanner = null;
    this.message = null;
    this.bossBannerT = 0;
    this.loading = true;
    // BGM 懒加载门：进关前硬等本关两首（道中曲 + boss 曲；加载提示复用 LOADING 画面）。
    // 后台 4 并发队列平时已预热，这里通常零等待；ensureAudio 失败也 resolve（缺失静默）。
    {
      const stage = this._loadStageModule(idx);
      this.stage = stage;
      this.state.stageName = stage.name;
      this.bgKey = stage.bg || `bg${idx + 1}`;
      this.bgmKey = this._bgmKey(stage.bgm, idx);
      const bossKey = (this.extra || (this.practice && idx === 6)) ? 'bgm_boss' : `bgm_boss${idx + 1}`; // 与 _loadStageModule 的 exLike 对齐（Ex 练习不算 bgm_boss7）
      Promise.all([Assets.ensureAudio(this.bgmKey), Assets.ensureAudio(bossKey)]).catch((e) => {
        console.warn('[gameScene] BGM 懒加载门异常（照常进关）:', e);
      }).then(() => {
      this.audio.playBgm(this.bgmKey);
      const s = this.state.scoring;
      s.startChapter();
      this._chapterStart = {
        pointItems: s.pointItems,
        graze: s.graze,
        captured: s.spellCardsCaptured,
      };
      if (this.practice) {
        // 符卡练习：不跑关卡脚本，直接生成所选符卡
        this._gen = null;
        this._scriptDone = true;
        this._waitT = 0;
        this._waitingClear = false;
        this._waitingBoss = false;
        this.titleCardT = 0;
        this.loading = false;
        this._spawnPracticeBoss();
        return;
      }
      this._gen = stage.script(this.ctx);
      this._scriptDone = false;
      this._waitT = 0;
      this._waitingClear = false;
      this._waitingBoss = false;
      this.titleCardT = TITLE_CARD_T;
      this.loading = false;
      });
    }
  },

  /** 符卡练习：按选择生成单符卡 boss（保留 def.mid：道中符卡用道中曲，与正篇一致）。 */
  _spawnPracticeBoss() {
    const p = this.practice;
    const def = p.stageIndex === 6
      ? (p.kind === 'mid' ? BF.s6Mid() : BF.exBig()) // EXTRA 道中（彗星骑士）/大 boss 按 kind 分派
      : BF[`s${p.stageIndex + 1}${p.kind === 'mid' ? 'Mid' : 'Big'}`]();
    const spells = def.phases.filter((ph) => ph.spellCard);
    const chosen = spells[Math.min(p.phaseIndex, spells.length - 1)];
    this._spawnBoss({ ...def, phases: [chosen] });
  },

  /** 关卡 bgm 字段归一化为 manifest key（兼容 'stage1' 与 'bgm_stage1' 两种写法）。 */
  _bgmKey(bgm, idx) {
    const raw = bgm || `stage${idx + 1}`;
    return raw.startsWith('bgm_') ? raw : `bgm_${raw}`;
  },

  // ---------------------------------------------------------------- 脚本驱动

  _advanceScript() {
    if (this.loading || this._scriptDone) return;
    for (;;) {
      if (this._waitT > 0 || this._waitingClear || this._waitingBoss) return;
      const next = this._gen.next();
      if (next.done) {
        this._scriptDone = true;
        return;
      }
      const cmd = next.value || {};
      if (cmd.wait) { this._waitT = cmd.wait; return; }
      if (cmd.spawn) {
        this.enemies.spawn(cmd.spawn);
        this.state.scoring.registerEnemySpawn(1);
        continue;
      }
      if (cmd.wave) {
        for (const d of cmd.wave) {
          this.enemies.spawn(d);
          this.state.scoring.registerEnemySpawn(1);
        }
        continue;
      }
      if (cmd.clear) { this._waitingClear = true; return; }
      if (cmd.boss) {
        if (this._tryStartDialogue(cmd.boss)) return; // 大 boss 战前对话（练习跳过）
        this._spawnBoss(cmd.boss);
        this._waitingBoss = true;
        return;
      }
      if (cmd.say) { this.message = { text: L(cmd.say), t: 2.5 }; continue; }
      // 未知指令：跳过
    }
  },

  /** 敌机射击门控：遵守睡眠/混乱效果；对象池复用时校正过期的 _nextFire。 */
  _fireGate(e, interval) {
    // 与 bosses.js fireTimer 同一门控（game/modsGate.js）：fallback 关用
    e._nextFire = (e._nextFire === undefined || e._nextFire > e.age + interval) ? e.age : e._nextFire;
    if (e.age >= e._nextFire) {
      e._nextFire = e.age + gatedInterval(interval);
      return true;
    }
    return false;
  },

  _spawnBoss(def) {
    // Boss 阶段击破时清弹 → EBB（永夜抄公式）+ 转道具。
    // 通过 field 代理拦截 clearToItems：Boss 内部调用即触发结算。
    if (!this._fieldProxy) {
      const real = this.field;
      const scene = this;
      const proxy = Object.create(real);
      proxy.clearToItems = (items, cx, cy) => {
        const n = real.count;
        if (n > 0) {
          const bonus = scene.state.scoring.enemyBulletBonus(n);
          if (bonus > 0) scene.effects.floatText(VIEW_W / 2, 180, `BULLET BONUS +${bonus}`, '#ff4');
        }
        return real.clearToItems(items, cx, cy);
      };
      this._fieldProxy = proxy;
    }
    const bossCtx = Object.create(this.ctx);
    bossCtx.field = this._fieldProxy;
    bossCtx.stageIndex = this.stageIndex + 1; // 符卡初值按面数
    bossCtx.difficulty = this.diff.spellTier; // 符卡 bonus 难度项
    bossCtx.diffIdx = this.diffIdx; // 弹幕难度分支（bosses.js 组件开关）
    bossCtx.spellHistory = readSpellHistory(); // 符卡挑战历史（Boss.draw 显示用）
    const scene = this;
    this.boss = new Boss({
      ...def, // 血量不随难度变化（与东方一致）：难度差异由弹幕结构承载
      ctx: bossCtx,
      onDefeated(boss) {
        scene.effects.explosion(boss.x, boss.y, 3);
        scene.effects.flash(0.6);
        scene.audio.playSfx('explosion');
        if (scene.bgmKey) scene.audio.playBgm(scene.bgmKey);
      },
      onPhaseChange(idx, ph) {
        // 阶段宣言：符卡大字宣言（粉色 + 特殊音效）；非符小字白显（无声）。
        // alarm 只留给 boss 出场 WARNING（_spawnBoss 末尾），不再每阶段响。
        scene.phaseBanner = { text: L(ph.name), spell: !!ph.spellCard, t: ph.spellCard ? 2.2 : 1.3 };
        if (ph.spellCard) scene.audio.playSfx('special');
      },
      onSpellCardResult(bonus) {
        if (bonus > 0) {
          scene.effects.floatText(VIEW_W / 2, 150, `SPELL CARD BONUS +${bonus}`, '#f4c');
        } else {
          scene.effects.floatText(VIEW_W / 2, 150, 'SPELL CARD FAILED…', '#888');
        }
        // 符卡挑战历史（东方惯例）：正常模式才记（练习模式由 _practiceEnd 记、
        // 回放旁观不记）；key 与练习模式共用
        if (!scene.practice && !scene.replayer && scene.boss) {
          const k = spellKey(scene.stageIndex, def.mid ? 'mid' : 'big', spellIndexOf(scene.boss));
          recordSpellResult(k, bonus > 0);
          if (scene.boss.ctx) scene.boss.ctx.spellHistory = readSpellHistory(); // 刷新显示
        }
      },
    });
    this.boss.kind = def.mid ? 'mid' : 'big'; // Boss.draw 历史键用
    this.boss.spellStageIndex = this.stageIndex; // 0 基面数
    this.bossBannerT = 3;
    this.audio.playSfx('alarm'); // WARNING 警报只在 boss 出场响（阶段宣言另有 special 音）
    // 中 boss 不切关卡 BGM（道中曲贯穿道中）；最终 boss 切每关独立 boss 曲
    // （bgm_bossN），未加载时退回通用 bgm_boss；Extra 道中用 bgm_ex_stage，
    // boss 战切 bgm_boss「决战时刻」（extra 时 has 恒 false，正落在此曲）
    if (!def.mid) {
      const bossBgm = `bgm_boss${this.stageIndex + 1}`;
      const has = !this.extra && this.assets && typeof this.assets.audio === 'function' && this.assets.audio(bossBgm);
      this.audio.playBgm(has ? bossBgm : 'bgm_boss');
    }
  },

  // ---------------------------------------------------------------- 主更新

  update(dt) {
    if (this.loading) return;
    if (this._updateReplay()) return;
    if (this._updatePracticeEnd()) return;
    if (this.mode === 'dialogue') {
      this._updateDialogue(dt);
      return;
    }
    if (this.mode === 'continue') {
      this._updateContinue(dt);
      return;
    }
    if (this.paused) {
      this._updatePause();
      return;
    }
    if (Input.pressed('pause') && this.mode === 'play') {
      this.paused = true;
      this.pauseIdx = 0;
      this.audio.playSfx('select');
      return;
    }

    this.t += dt;
    this.ctx.t = this.t;
    this.ctx.stageIndex = this.stageIndex;

    this._decayTimers(dt);
    this._updateDash();
    this._decayBanners(dt);
    this._updateScript(dt);

    this._updatePlayer(dt);
    this._updateActors(dt);
    this._updateShots(dt);

    this.items.update(dt, { playerX: this.player.x, playerY: this.player.y, viewH: VIEW_H, magnetRadius: this.player.magnetR });
    this.effects.update(dt);

    this._playFeedbackSfx();
    this._checkExtend();

    if (this._checkPracticeBomb()) return;
    if (this._checkPracticeEnd()) return;
    if (this._checkGameOver()) return;
    this._checkStageClear();
    this._updateTally(dt);
  },

  /**
   * 回放/录像输入驱动；返回 true = 本帧中断（跳过后续更新）。
   * 回放：注入录像输入（真实键盘 X/P/Esc 中断退出——按物理键码判断，
   * 避免录像里的 X（bomb）/P（暂停）被误判为中断指令）；录像：逐逻辑步记录输入
   */
  _updateReplay() {
    if (this.replayer) {
      if (Input._codesDown.has('KeyX') || Input._codesDown.has('KeyP') || Input._codesDown.has('Escape')) {
        this.mgr.switchTo('replayList');
        return true;
      }
      this.replayer.apply(Input);
    } else if (this.recorder) {
      this.recorder.capture(Input);
    }
    return false;
  },

  /** 符卡练习结算页：Z 再来一次，X 返回选择；返回 true = 结算页模式（跳过后续更新）。 */
  _updatePracticeEnd() {
    if (this.mode !== 'practiceEnd') return false;
    if (Input.pressed('confirm')) {
      this.mgr.switchTo('game', { character: this.character, difficulty: this.diff, mode: 'practice', practice: this.practice });
    } else if (Input.pressed('cancel')) {
      this.mgr.shared.practiceReturn = true; // 一次性：练习菜单据此恢复上次练习的卡（仅从练习返回时）
      this.mgr.switchTo('spellPractice');
    }
    return true;
  },

  /** 计时器衰减：延时演出计时器 + 角色技能计时器。 */
  _decayTimers(dt) {
    // 延时演出计时器
    for (let i = this.timers.length - 1; i >= 0; i--) {
      const tm = this.timers[i];
      tm.t -= dt;
      if (tm.t <= 0) {
        this.timers.splice(i, 1);
        tm.fn();
      }
    }
    // 技能计时器衰减（归零瞬间给结束提示，持续期结束必须可辨认）
    for (const k in this.mods) {
      if (this.mods[k] > 0) {
        this.mods[k] = Math.max(0, this.mods[k] - dt);
        if (this.mods[k] === 0) {
          this.effects.floatText(this.player.x, this.player.y - 24, t('mod.end'), '#889');
        }
      }
    }
  },

  /** 开开心·无敌冲刺：移速↑ + 接触伤敌（基准速按角色 stats）。 */
  _updateDash() {
    const dashing = this.mods.dashT > 0;
    this.player.speedFast = dashing ? 7 : this.player.baseSpeedFast;
    if (dashing) {
      const hits = this.enemies.queryHit(this.player.x, this.player.y, 18);
      for (const e of hits) this.enemies.damage(e, 4, this.ctx);
      // 像素残影：每 0.05s 留一帧（同一像素帧渐隐叠印，像素美学的冲刺特效）
      const lean = this.player._lean || 0;
      const dir = lean > 0.3 ? '-r' : lean < -0.3 ? '-l' : '';
      const base = this.player.character && this.player.character.spriteKey;
      const img = base && (this.assets.img(dir ? base + dir : base) || this.assets.img(base));
      if (img) {
        this.player._trail = this.player._trail || [];
        this.player._trailT = (this.player._trailT || 0) - 1 / 60;
        if (this.player._trailT <= 0) {
          this.player._trailT = 0.05;
          this.player._trail.unshift({ img, x: this.player.x, y: this.player.y, alpha: 0.45 });
          if (this.player._trail.length > 6) this.player._trail.pop();
        }
        for (const tr of this.player._trail) tr.alpha *= 0.88;
      }
    } else if (this.player._trail) {
      this.player._trail = null;
    }
  },

  /** 演出计时衰减（标题卡/boss 横幅/阶段宣言/台词框）+ 背景滚动。 */
  _decayBanners(dt) {
    if (this.titleCardT > 0) this.titleCardT -= dt;
    if (this.bossBannerT > 0) this.bossBannerT -= dt;
    if (this.phaseBanner && this.phaseBanner.t > 0) this.phaseBanner.t -= dt;
    if (this.message) {
      this.message.t -= dt;
      if (this.message.t <= 0) this.message = null;
    }
    this.bgScroll += BG_SCROLL_SPEED * dt;
  },

  /** 关卡脚本：推进等待条件后驱动 generator。 */
  _updateScript(dt) {
    if (this._waitT > 0) this._waitT -= dt;
    if (this._waitingClear && this.enemies.count === 0) this._waitingClear = false;
    if (this._waitingBoss && (!this.boss || !this.boss.alive)) this._waitingBoss = false;
    this._advanceScript();
  },

  /** 自机推进 + 符卡判脏 + 护盾/梦游挡死 + ctx 同步。 */
  _updatePlayer(dt) {
    // 自机（移动/射击/炸弹/特殊/中弹/擦弹由 Player 内部处理）
    const livesBefore = this.state.lives;
    const bombsBefore = this.state.bombs;
    const specialBefore = this.state.special;
    this.player.update(dt, this.ctx);
    // 中弹/用 bomb/用技能 → boss 符卡判脏（无伤无雷无技能才有奖励）
    if ((this.state.lives < livesBefore || this.state.bombs < bombsBefore
      || this.state.special < specialBefore) && this.boss) {
      this.boss.phaseDirty = true;
      // 显著的奖励分取消提示（每符卡只报一次，用户定案要醒目）
      if (this.boss.phase && this.boss.phase.spellCard && !this.boss._failToastShown) {
        this.boss._failToastShown = true;
        this.effects.floatText(this.player.x, this.player.y - 40, t('spell.bonusLost'), '#f88', 2.0);
        this.audio.playSfx('death', { volume: 0.5 });
      }
    }
    // 泪泪·护盾 / 困困·梦游：挡下一次中弹（deathbomb 窗口内取消）
    if (this.player._pendingDeath >= 0) {
      if (this.mods.shieldT > 0) {
        this.mods.shieldT = 0;
        this.player._pendingDeath = -1;
        this.player.invulnUntil = this.player.time + 1;
        this.effects.flash(0.3);
        this.effects.floatText(this.player.x, this.player.y - 20, t('float.shield'), '#6cf');
        this.audio.playSfx('hit');
      }
    }

    this.ctx.playerX = this.player.x;
    this.ctx.playerY = this.player.y;
    this.ctx.boss = this.boss; // 自机体术判定用（player.update 读）
  },

  /** boss / 敌机 / 敌弹场推进 + 惑惑·思维混乱弹向错乱。 */
  _updateActors(dt) {
    if (this.boss) this.boss.update(dt);
    this.enemies.update(dt, this.ctx);
    this.field.update(dt, this.ctx);

    // 惑惑·思维混乱：新生敌弹方向错乱（随机摆动，按 id 水位只附一次 onUpdate 包装）
    if (this.mods.jitterT > 0) {
      let maxId = this._jitterMark;
      this.field.forEachActive((b) => {
        if (b.id > this._jitterMark) {
          const prev = b.onUpdate;
          const ph = rand() * TAU;
          b.onUpdate = (bb, dt, c) => {
            if (prev) prev(bb, dt, c);
            if (!bb._active) return;
            const rot = Math.sin(bb.age * 6 + ph) * 1.8 * dt;
            const cos = Math.cos(rot);
            const sin = Math.sin(rot);
            const vx = bb.vx * cos - bb.vy * sin;
            bb.vy = bb.vx * sin + bb.vy * cos;
            bb.vx = vx;
          };
          if (b.id > maxId) maxId = b.id;
        }
      });
      this._jitterMark = maxId;
    }
  },

  /** 自机弹推进（追踪目标：boss 优先，否则最近敌人）+ 命中判定。 */
  _updateShots(dt) {
    let tx;
    let ty;
    if (this.boss && this.boss.alive) {
      tx = this.boss.x;
      ty = this.boss.y;
    } else {
      let best = Infinity;
      this.enemies.forEachActive((e) => {
        const d = (e.x - this.player.x) ** 2 + (e.y - this.player.y) ** 2;
        if (d < best) {
          best = d;
          tx = e.x;
          ty = e.y;
        }
      });
    }
    this.playerShots.update(dt, tx !== undefined ? { targetX: tx, targetY: ty } : {});
    this._hitEnemies();
  },

  /** 音效：擦弹 / 射击（按计数增量触发，引擎侧 30ms 去抖）。 */
  _playFeedbackSfx() {
    if (this.state.scoring.graze > this._grazeSeen) {
      this._grazeSeen = this.state.scoring.graze;
      this.audio.playSfx('graze', { volume: 0.5 });
    }
    // 开火音效按角色区分（shot_<id>，缺省回落通用 shoot）
    if (this.playerShots.count > this._shotsSeen) {
      const key = this.character && this.character.id ? `shot_${this.character.id}` : 'shoot';
      this.audio.playSfx(key, { volume: 0.35 });
    }
    this._shotsSeen = this.playerShots.count;
  },

  /** 奖残机（点道具累计曲线）+ 最大点道具值记录。 */
  _checkExtend() {
    const ext = this.state.scoring.checkExtend();
    if (ext > 0) {
      this.state.lives += ext;
      this.audio.playSfx('extend');
      this.effects.floatText(this.player.x, this.player.y - 30, 'EXTEND!', '#f6c');
    }
    if (this.state.scoring.pointV > this.maxPointV) this.maxPointV = this.state.scoring.pointV;
  },

  /** 符卡练习：boss 击破 → 结算；1 命死亡 → 失败。返回 true = 已结算（跳过后续更新）。 */
  _checkPracticeEnd() {
    if (this.practice && this.mode === 'play') {
      if (this.boss && !this.boss.alive) {
        this._practiceEnd(true);
        return true;
      }
      if (!this.player.alive) {
        this._practiceEnd(false);
        return true;
      }
    }
    return false;
  },

  /** 符卡练习中用雷/技能 → 立刻失败（用户定案：不是等结算才判，而是当场结束练习）。
   *  返回 true = 已结算（跳过后续更新）。 */
  _checkPracticeBomb() {
    if (this.practice && this.mode === 'play') {
      const bombUsed = Input.pressed('bomb') && this.state.bombs > 0;
      const specialUsed = Input.pressed('special') && this.state.special >= SPECIAL_MAX;
      if (bombUsed || specialUsed) {
        // 清掉本次输入，避免失败结算页外还触发 bomb/special
        Input.justPressed.delete('bomb');
        Input.justPressed.delete('special');
        this._practiceEnd(false);
        return true;
      }
    }
    return false;
  },

  /** 残机尽 → 续关流程（Extra 无续关，直接结算）。返回 true = 进入续关/结算（跳过后续更新）。 */
  _checkGameOver() {
    if (!this.player.alive && this.mode === 'play') {
      if (this.extra) {
        this._gotoResult(false);
        return true;
      }
      // 先归一时间轴再清延时器：酷总子弹时间的恢复回调也走 timers，
      // 顺序反了会把游戏永久卡在 0.35×（历史 bug）；bomb 多段伤害亦不拖进新一局
      if (this.mgr.shared.loop) this.mgr.shared.loop.timeScale = 1;
      this.timers.length = 0;
      this.mode = 'continue';
      this.continueT = CONTINUE_T;
      return true;
    }
    return false;
  },

  /** 过关判定：脚本跑完 + 场上无敌 + 自机存活。 */
  _checkStageClear() {
    if (this.mode === 'play' && this._scriptDone && this.enemies.count === 0
      && (!this.boss || !this.boss.alive) && this.player.alive) {
      this._finishStage();
    }
  },

  /** 结算卡倒计时。 */
  _updateTally(dt) {
    if (this.mode === 'tally') {
      this.tallyT -= dt;
      // 首帧不响应 confirm：击破帧的射击 Z 边沿不得同帧跳过结算卡（历史 bug）
      if (this.tallyT <= 0 || (Input.pressed('confirm') && this.tallyT < TALLY_T - 0.2)) this._nextStage();
    }
  },

  /** 自机弹 vs 敌人 / boss（命中迸命中火花）。 */
  _hitEnemies() {
    const scene = this;
    this.playerShots.forEachActive((s) => {
      // 贯通弹按目标去重：同一目标只结算一次（pierce = 可命中的不同目标数）
      const hits = scene.enemies.queryHit(s.x, s.y, s.r).filter((e) => !s._hitUids.has(e._uid));
      if (hits.length > 0) {
        // 按本帧新命中目标数扣 pierce：同帧重叠多目标不得超过 pierce+1（审计发现：
        // 此前只扣 1，敌群聚集时一颗 pierce=2 弹可同帧命中任意多目标）
        const targets = hits.slice(0, s.pierce + 1);
        for (const e of targets) {
          s._hitUids.add(e._uid);
          scene.enemies.damage(e, s.damage, scene.ctx);
        }
        scene.audio.playSfx('hit', { volume: 0.3 });
        scene.effects.hitSpark(s.x, s.y, '#fc6');
        s.pierce -= targets.length;
        if (s.pierce < 0) scene.playerShots.pool.release(s);
        return;
      }
      const boss = scene.boss;
      if (boss && boss.alive && !s._hitUids.has(boss._uid)) {
        const dx = s.x - boss.x;
        const dy = s.y - boss.y;
        const rr = s.r + boss.r;
        if (dx * dx + dy * dy <= rr * rr) {
          if (boss.age < boss.invulnUntil) {
            // 无敌帧弹开：蓝白火花 + 高音 clink（与正常命中的橙花低音严格区分）
            scene.audio.playSfx('hit', { volume: 0.15, rate: 1.8 });
            scene.effects.hitSpark(s.x, s.y, '#9df', { n: 5, sizeMul: 1.8, lifeMul: 1.8 });
          } else {
            s._hitUids.add(boss._uid);
            boss.damage(s.damage);
            scene.audio.playSfx('hit', { volume: 0.2 });
            scene.effects.hitSpark(s.x, s.y, '#fc6');
          }
          if (s.pierce > 0) s.pierce--;
          else scene.playerShots.pool.release(s);
        }
      }
    });
  },

  _onCollect(it) {
    // 1UP 独立反馈：大字 + 奖残音效（通用拾取反馈不足以感知命 +1）
    if (it.type === '1UP') {
      this.state.collectItem(it, VIEW_H);
      this.effects.floatText(it.x, it.y - 10, '1UP!', '#f6c');
      this.audio.playSfx('extend');
      return;
    }
    // P/B 道具无分数返回值（v=0），但同样需要拾取视觉反馈（审计发现：此前只有音效）
    if (it.type === 'P') {
      const wasMax = this.state.power >= POWER_MAX;
      this.state.collectItem(it, VIEW_H);
      this.effects.floatText(it.x, it.y, `+${POWER_PER_ITEM}P`, '#6cf');
      // 火力刚吃满：显著提示（ Touhou 惯例 FULL POWER 提示）
      if (!wasMax && this.state.power >= POWER_MAX) {
        this.effects.floatText(this.player.x, this.player.y - 30, 'POWER FULL!', '#ff4');
        this.audio.playSfx('extend', { volume: 0.5 });
      }
      this.audio.playSfx('pickup', { volume: 0.4 });
      return;
    }
    if (it.type === 'B') {
      this.state.collectItem(it, VIEW_H);
      this.effects.floatText(it.x, it.y, '+1 BOMB', '#8f8');
      this.audio.playSfx('pickup', { volume: 0.4 });
      return;
    }
    const v = this.state.collectItem(it, VIEW_H);
    if (v > 0) {
      this.effects.floatText(it.x, it.y, `+${v}`, it.type === 'STAR' ? '#fc3' : '#8cf');
    }
    this.audio.playSfx('pickup', { volume: 0.4 });
  },

  // ---------------------------------------------------------------- 过关 / 续关

  _finishStage() {
    // 过关自动吸取屏上剩余道具（结算期间玩家无暇拾取）
    this.items.forEachActive((it) => this._onCollect(it));
    this.items.clear();

    const s = this.state.scoring;
    const chapter = s.endChapter();
    // chapter bonus ≥100 万 → 炸弹碎片；3 个合成 1 bomb
    let fragMsg = null;
    if (chapter.bombFragment) {
      this.bombFragments++;
      fragMsg = tf('tally.fragLine', { n: this.bombFragments });
      if (this.bombFragments >= 3) {
        this.bombFragments -= 3;
        this.state.addBomb(1);
        fragMsg = t('tally.fragMerge');
        this.audio.playSfx('extend');
      }
    }
    const isFinal = this.extra || this.stageIndex === STAGE_COUNT - 1;
    const d = this._chapterStart;
    const tally = s.stageTally({
      stageIndex: this.stageIndex + 1,
      pointItems: s.pointItems - d.pointItems,
      graze: s.graze - d.graze,
      isFinal,
      lives: this.state.lives,
      bombs: this.state.bombs,
    });
    this.tallyInfo = {
      tally,
      pointItems: s.pointItems - d.pointItems,
      graze: s.graze - d.graze,
      captured: s.spellCardsCaptured - d.captured,
      chapterBonus: chapter.bonus,
      fragMsg,
      isFinal,
    };
    this.mode = 'tally';
    this.tallyT = TALLY_T;
    this.player.invulnUntil = this.player.time + TALLY_T + 2; // 结算期间免伤
    this.audio.playSfx('select');
    this.audio.playBgm('bgm_clear', { loop: false }); // 过关结尾曲（非循环，播完即止）
  },

  _nextStage() {
    if (this.extra || (this.tallyInfo && this.tallyInfo.isFinal)) {
      this._gotoResult(true);
      return;
    }
    this.mode = 'play';
    this.boss = null;
    this._fieldProxy = null;
    this._startStage(this.stageIndex + 1);
  },

  // ---------------------------------------------------------------- 战前对话（mode='dialogue'）

  /** 大 boss 战前对话触发：练习/中 boss/无剧本返回 false。 */
  _tryStartDialogue(def) {
    if (this.practice || def.mid) return false;
    const key = this.extra ? 'extra' : this.stageIndex + 1;
    const lines = STAGE_DIALOGUE[key];
    if (!lines || !lines.length) return false;
    this._pendingBossDef = def;
    this._dlg = { lines, idx: 0, chars: 0, hold: 0 };
    this.mode = 'dialogue';
    return true;
  },

  /** 对话推进：打字机（40 字/s）+ 满行 1.4s 自动下一行（逻辑帧计时，回放确定）；
   *  Z 补全/下一行，X 跳过整段。 */
  _updateDialogue(dt) {
    const d = this._dlg;
    if (!d) { this._endDialogue(); return; } // 兜底不静默吞 boss（audit-fix）
    if (Input.pressed('cancel')) { this._endDialogue(); return; }
    // 计时按未本地化的原文长度（审计发现：用 L() 译文长度会让对话帧数随语言变化，
    // 中文录像在 en 环境（服务端校验）整体错位 → 真实成绩被误改写）。L() 仅用于展示。
    if (!d.src) {
      d.src = dialogueText(d.lines[d.idx], this.character.id);
      d.full = L(d.src);
      d.wrapped = wrapText(d.full, VIEW_W - 76, 13);
    }
    if (d.chars < d.src.length) {
      d.chars = Math.min(d.src.length, d.chars + dt * 40);
      if (Input.pressed('confirm')) d.chars = d.src.length;
      return;
    }
    d.hold += dt;
    if (Input.pressed('confirm') || d.hold > 1.4) {
      d.idx++;
      d.chars = 0;
      d.hold = 0;
      d.src = null; // 下行重算缓存
      if (d.idx >= d.lines.length) this._endDialogue();
    }
  },

  _endDialogue() {
    const def = this._pendingBossDef;
    this._pendingBossDef = null;
    this._dlg = null;
    this.mode = 'play';
    this._spawnBoss(def);
    this._waitingBoss = true;
  },

  _updateContinue(dt) {
    this.continueT -= dt;
    if (Input.pressed('confirm') && this.continues < MAX_CONTINUES) {
      // 续关：分数归零、次数+1、残机炸弹重置、重开本关
      this.continues++;
      this.state.scoring.score = 0;
      this.state.lives = DEFAULT_LIVES;
      this.state.bombs = DEFAULT_BOMBS;
      this.field.pool.clear();
      this.enemies.clear();
      this.items.clear();
      this.boss = null;
      this._fieldProxy = null;
      this.timers.length = 0; // 同上：延时伤害不进新一局
      this.player.reset(VIEW_W / 2, VIEW_H - 80, this.character);
      this.mode = 'play';
      this.audio.playSfx('select');
      this._startStage(this.stageIndex);
      return;
    }
    if (Input.pressed('cancel') || this.continueT <= 0) {
      this._gotoResult(false);
    }
  },

  /** 符卡练习结算：记录结果 + 录像仅本地保存（不上传/不上榜）。 */
  _practiceEnd(defeated) {
    if (this.replayer) { // 旁观练习录像：不污染符卡历史、不落可交互结算页，直接回列表
      this.mgr.switchTo('replayList');
      return true;
    }
    const captured = defeated && this.boss && !this.boss.phaseDirty && this.boss.phaseTime < this.boss.timeLimit;
    this.practiceResult = { defeated, captured };
    this.mode = 'practiceEnd';
    // 符卡历史（本地）：挑战次数 +1，取得则成功次数 +1（东方符卡列表惯例）
    recordSpellResult(`${this.practice.stageIndex}-${this.practice.kind}-${this.practice.phaseIndex}`, captured);
    if (this.recorder) {
      const payload = this.recorder.uploadPayload({
        score: Math.floor(this.state.scoring.score),
        stageReached: this.practice.stageIndex + 1,
        cleared: defeated,
      });
      payload.practice = this.practice;
      try {
        const KEY = 'kimi-replays';
        const list = JSON.parse(globalThis.localStorage?.getItem(KEY) || '[]');
        list.unshift({ id: `local-${Date.now()}`, date: new Date().toISOString(), ...payload });
        globalThis.localStorage?.setItem(KEY, JSON.stringify(list.slice(0, 10)));
      } catch (e) { /* 存储失败忽略 */ }
    }
  },

  _gotoResult(cleared) {
    // 回放结束：不进结算，直接回回放列表
    if (this.replayer) {
      this.mgr.switchTo('replayList');
      return;
    }
    const s = this.state.scoring;
    const stageReached = this.extra ? 1 : cleared ? STAGE_COUNT : this.stageIndex + 1;
    const resultParams = {
      score: Math.floor(s.score),
      graze: s.graze,
      pointV: s.pointV,
      maxPointV: this.maxPointV,
      captured: s.spellCardsCaptured,
      attempted: s.spellCardsAttempted,
      continues: this.continues,
      cleared,
      stageReached,
      character: this.character.id,
      difficulty: this.diff.id,
      // 录像 payload：结算场景负责本地保存 + 上传
      replayPayload: this.recorder
        ? this.recorder.uploadPayload({ score: Math.floor(s.score), stageReached, cleared })
        : null,
    };
    // 通关后先播放制作名单（可跳过），再进结算
    if (cleared) {
      this.mgr.switchTo('credits', { next: 'result', params: resultParams });
      return;
    }
    this.mgr.switchTo('result', resultParams);
  },

  // ---------------------------------------------------------------- 暂停

  _updatePause() {
    const items = PAUSE_ITEMS;
    const nav = menuNav(Input, this.audio, this.pauseIdx, items.length);
    if (nav !== null) this.pauseIdx = nav;
    if (Input.pressed('pause')) {
      this.paused = false;
      return;
    }
    if (Input.pressed('confirm')) {
      this.audio.playSfx('select');
      if (this.pauseIdx === 0) {
        this.paused = false;
      } else if (this.pauseIdx === 1) {
        // 重开本关：清场 + 自机复位 + 重跑脚本（分数/残机保持）
        this.field.pool.clear();
        this.enemies.clear();
        this.items.clear();
        this.boss = null;
        this._fieldProxy = null;
        this.timers.length = 0; // 同上：延时伤害不进新一局
        this.player.reset(VIEW_W / 2, VIEW_H - 80, this.character);
        this.paused = false;
        this.mode = 'play';
        this._startStage(this.stageIndex);
      } else {
        this.paused = false;
        this.mgr.switchTo('title');
      }
    }
  },

  // ---------------------------------------------------------------- 绘制

  draw(r) {
    this._drawBackground(r);
    this._drawEntities(r);
    this._drawWatermark(r);
    this._drawBanners(r);
    this._drawDialogue(r);
    this._drawPracticeEnd(r); // 全屏遮罩在横幅之后：冻结的符卡宣言不得叠在结算页上（历史 bug）
    this._drawTally(r);
    this._drawContinue(r);
    this._drawPauseMenu(r);

    // 加载中
    if (this.loading) {
      r.text(t('game.loading'), VIEW_W / 2, VIEW_H / 2, { size: 16, color: '#8cf', align: 'center' });
    }
  },

  /** 背景竖向滚动（双份拼接，重叠 2px 防止亚像素接缝露底）。 */
  _drawBackground(r) {
    const bg = this.assets.img(this.bgKey || 'bg1');
    if (bg) {
      const sc = Math.max(VIEW_W / bg.width, VIEW_H / bg.height);
      const h = bg.height * sc;
      const y = (this.bgScroll % h) - h;
      r.sprite(bg, 0, y, { scale: sc, anchorX: 0, anchorY: 0 });
      r.sprite(bg, 0, y + h - 2, { scale: sc, anchorX: 0, anchorY: 0 });
    }
  },

  /** 实体绘制栈（道具→敌→boss→自机弹→自机→敌弹→特效→HUD）+ boss 位置指示。 */
  _drawEntities(r) {
    this.items.draw(r, this.assets);
    this.enemies.draw(r, this.assets);
    if (this.boss) this.boss.draw(r, this.assets);
    // 敌机状态像素符号（像素美学，不叠滤镜）：睡眠 Zzz / 混乱 ?，头顶浮动
    if (this.mods.sleepT > 0 || this.mods.confuseT > 0) {
      const mark = (x, y, i) => {
        // 深色描边再浅色字：亮色背景下可读（用户反馈白底白字不可见）
        const bob = Math.sin(this.t * 4 + i) * 2;
        const txt = this.mods.sleepT > 0 ? 'Zz' : '?';
        const col = this.mods.sleepT > 0 ? '#e6ccff' : '#9f9';
        const size = this.mods.sleepT > 0 ? 14 : 15;
        const tx = this.mods.sleepT > 0 ? x + 8 : x - 4;
        const ty = y - 18 + bob;
        for (const [ox, oy] of [[-1, 0], [1, 0], [0, -1], [0, 1]]) {
          r.text(txt, tx + ox, ty + oy, { size, color: '#201028', alpha: 0.9 });
        }
        r.text(txt, tx, ty, { size, color: col, alpha: 1 });
      };
      let i = 0;
      this.enemies.forEachActive((e) => mark(e.x, e.y, i++));
      if (this.boss && this.boss.alive) mark(this.boss.x, this.boss.y - 24, 99);
    }
    this.playerShots.draw(r, this.assets);
    this.player.draw(r, this.assets);
    this.field.draw(r, this.assets); // 敌弹在自机上方（擦弹可读性）
    this.effects.draw(r);
    drawHud(r, this.state, null); // boss 血条由 Boss.draw 自带，避免重复

    // boss 位置指示（东方风）：屏幕底缘标记 boss 横向位置
    if (this.boss && this.boss.alive) {
      const pulse = 0.6 + 0.4 * Math.sin(this.t * 8);
      r.rect(this.boss.x - 2, VIEW_H - 5, 4, 5, { fill: '#f66', alpha: pulse });
    }
  },

  /** 回放/练习水印（右下，避开顶栏分数/倍率与 boss 血条）。 */
  _drawWatermark(r) {
    if (this.replayer) {
      r.text('▶ REPLAY', VIEW_W - 8, VIEW_H - 32, { size: 12, color: '#f6c', align: 'right', alpha: 0.8 });
    } else if (this.practice) {
      r.text('PRACTICE', VIEW_W - 8, VIEW_H - 32, { size: 11, color: '#8cf', align: 'right', alpha: 0.7 });
    }
  },

  /** 符卡练习结算页。 */
  _drawPracticeEnd(r) {
    if (this.mode === 'practiceEnd' && this.practiceResult) {
      const res = this.practiceResult;
      r.rect(0, 0, VIEW_W, VIEW_H, { fill: '#000', alpha: 0.65 });
      r.text(res.defeated ? t('practice.clear') : t('practice.failed'), VIEW_W / 2, 240, { size: 26, color: res.defeated ? '#fc3' : '#f66', align: 'center' });
      if (res.defeated) {
        r.text(res.captured ? t('practice.captured') : t('practice.nobonus'), VIEW_W / 2, 292, { size: 16, color: res.captured ? '#f4c' : '#ccc', align: 'center' });
      }
      r.text(t('practice.retry'), VIEW_W / 2, 352, { size: 12, color: '#889', align: 'center' });
    }
  },

  /** 横幅层：关卡标题卡 / Boss 出场横幅 / 阶段宣言横幅 / 台词框。 */
  _drawBanners(r) {
    // 关卡标题卡
    if (this.titleCardT > 0 && this.stage) {
      const a = Math.min(1, this.titleCardT) * 0.9;
      r.rect(0, 250, VIEW_W, 90, { fill: '#000', alpha: a * 0.6 });
      r.text(L(this.stage.name), VIEW_W / 2, 268, { size: 24, color: '#fff', align: 'center', alpha: Math.min(1, this.titleCardT) });
      r.text(this.extra ? diffName(EXTRA_DIFF) : `STAGE ${this.stageIndex + 1} / ${STAGE_COUNT}`, VIEW_W / 2, 304, { size: 12, color: '#8cf', align: 'center', alpha: Math.min(1, this.titleCardT) });
    }

    // Boss 出场横幅
    if (this.boss && this.boss.alive && this.bossBannerT > 0) {
      r.text('WARNING', VIEW_W / 2, 200, { size: 26, color: '#f44', align: 'center', alpha: Math.min(1, this.bossBannerT) });
      r.text(`${L(this.boss.name)}${this.boss.title ? ' · ' + L(this.boss.title) : ''}`, VIEW_W / 2, 234, { size: 14, color: '#fcc', align: 'center', alpha: Math.min(1, this.bossBannerT) });
    }

    // 阶段宣言横幅（符卡大字粉色宣言 / 非符小字白显）
    if (this.phaseBanner && this.phaseBanner.t > 0) {
      const b = this.phaseBanner;
      if (b.spell) {
        r.rect(0, 254, VIEW_W, 52, { fill: '#201', alpha: Math.min(1, b.t) * 0.65 });
        r.text(b.text, VIEW_W / 2, 266, { size: 24, color: '#f6c', align: 'center', alpha: Math.min(1, b.t) });
      } else {
        r.text(b.text, VIEW_W / 2, 268, { size: 14, color: '#dde', align: 'center', alpha: Math.min(1, b.t * 1.5) * 0.85 });
      }
    }

    // 台词框
    if (this.message) {
      r.rect(40, VIEW_H - 120, VIEW_W - 80, 40, { fill: '#001', alpha: 0.8, stroke: '#46f' });
      r.text(this.message.text, VIEW_W / 2, VIEW_H - 108, { size: 13, color: '#dde', align: 'center' });
    }
  },

  /** 关卡结算卡。 */
  _drawDialogue(r) {
    if (this.mode !== 'dialogue' || !this._dlg) return;
    const d = this._dlg;
    const line = d.lines[d.idx];
    const full = d.full || L(dialogueText(line, this.character.id));
    r.rect(0, 0, VIEW_W, VIEW_H, { fill: '#000', alpha: 0.45 });
    // 立绘：左下自机、右上 boss；非说话方压暗
    const pImg = this.assets.img(this.character.sprite);
    if (pImg) r.sprite(pImg, 88, VIEW_H - 210, { scale: 1.6, alpha: line.who === 'player' ? 1 : 0.4 });
    const bImg = this.assets.img(this._pendingBossDef.spriteKey);
    if (bImg) r.sprite(bImg, VIEW_W - 100, 200, { scale: 1.2, alpha: line.who === 'boss' ? 1 : 0.4 });
    // 台词框
    r.rect(16, VIEW_H - 156, VIEW_W - 32, 138, { fill: '#001', alpha: 0.88, stroke: '#446' });
    const name = line.who === 'boss' ? L(this._pendingBossDef.name) : charName(this.character);
    r.text(name, 30, VIEW_H - 148, { size: 13, color: line.who === 'boss' ? '#f8a' : '#8cf' });
    // 打字机：整文折行后按揭示字数逐行截取（行宽不随字数跳动；折行在 _updateDialogue 缓存）
    const allLines = d.wrapped || wrapText(full, VIEW_W - 76, 13);
    // 揭示字数映射：计时走原文长度，展示按译文字长等比揭示
    const ratio = d.src && d.src.length ? Math.min(1, d.chars / d.src.length) : 1;
    let remain = Math.floor(full.length * ratio);
    for (let i = 0; i < allLines.length && remain > 0; i++) {
      r.text(allLines[i].slice(0, remain), 30, VIEW_H - 124 + i * 20, { size: 13, color: '#eef' });
      remain -= allLines[i].length;
    }
    if (ratio >= 1 && Math.sin(d.hold * 8) > 0) {
      r.text('▼', VIEW_W - 40, VIEW_H - 40, { size: 12, color: '#ff4' });
    }
  },

  _drawTally(r) {
    if (this.mode === 'tally' && this.tallyInfo) {
      const info = this.tallyInfo;
      r.rect(60, 140, VIEW_W - 120, 300, { fill: '#001', alpha: 0.92, stroke: '#fc3' });
      r.text(info.isFinal ? t('tally.allClear') : t('tally.stageClear'), VIEW_W / 2, 160, { size: 22, color: '#fc3', align: 'center' });
      const rows = [
        [`${t('tally.pointItems')} × ${info.pointItems}`, ''],
        [`${t('tally.graze')} × ${info.graze}`, ''],
        [`${t('tally.spell')} × ${info.captured}`, ''],
        [`CHAPTER BONUS`, `+${info.chapterBonus}`],
        [`STAGE BONUS`, `+${info.tally}`],
      ];
      if (info.fragMsg) rows.push(['', info.fragMsg]);
      for (let i = 0; i < rows.length; i++) {
        const y = 210 + i * 30;
        if (rows[i][0]) r.text(rows[i][0], 100, y, { size: 14, color: '#dde' });
        if (rows[i][1]) r.text(rows[i][1], VIEW_W - 100, y, { size: 14, color: '#ff4', align: 'right' });
      }
      r.text(t('tally.next'), VIEW_W / 2, 410, { size: 11, color: '#889', align: 'center' });
    }
  },

  /** 续关画面。 */
  _drawContinue(r) {
    if (this.mode === 'continue') {
      r.rect(0, 0, VIEW_W, VIEW_H, { fill: '#000', alpha: 0.7 });
      r.text('GAME OVER', VIEW_W / 2, 220, { size: 28, color: '#f66', align: 'center' });
      const left = Math.ceil(this.continueT);
      r.text(`CONTINUE?  ${left}`, VIEW_W / 2, 280, { size: 20, color: '#fff', align: 'center' });
      r.text(tf('cont.zline', { n: MAX_CONTINUES - this.continues }), VIEW_W / 2, 320, { size: 12, color: '#8cf', align: 'center' });
      r.text(t('cont.xline'), VIEW_W / 2, 340, { size: 12, color: '#889', align: 'center' });
    }
  },

  /** 暂停菜单。 */
  _drawPauseMenu(r) {
    if (this.paused) {
      const items = PAUSE_ITEMS;
      r.rect(0, 0, VIEW_W, VIEW_H, { fill: '#000', alpha: 0.6 });
      r.rect(140, 220, 200, 160, { fill: '#001', alpha: 0.95, stroke: '#46f' });
      r.text('PAUSE', VIEW_W / 2, 240, { size: 18, color: '#fff', align: 'center' });
      for (let i = 0; i < items.length; i++) {
        const y = 280 + i * 30;
        const sel = i === this.pauseIdx;
        if (sel) r.text('▶', 168, y, { size: 14, color: '#ff4' });
        r.text(t(items[i]), VIEW_W / 2, y, { size: 14, color: sel ? '#ff4' : '#ccc', align: 'center' });
      }
    }
  },
};
